import { useState, useEffect, Fragment } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'
import { rolesApi, screensApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { Screen, PermissionInput, CreateRoleRequest, UpdateRoleRequest } from '@/types'

interface PermissionState {
  [screenId: string]: {
    can_view: boolean
    can_add_edit: boolean
    can_approve: boolean
  }
}

interface CreateRolePageProps {
  mode?: 'create' | 'edit' | 'view'
}

export default function CreateRolePage({ mode = 'create' }: CreateRolePageProps) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  
  const [roleName, setRoleName] = useState('')
  const [roleDescription, setRoleDescription] = useState('')
  const [permissions, setPermissions] = useState<PermissionState>({})

  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'
  const isCreateMode = mode === 'create'

  // Fetch screens
  const { data: screens = [], isLoading: screensLoading } = useQuery({
    queryKey: ['screens'],
    queryFn: () => screensApi.getAll(),
  })

  // Fetch role details for edit/view mode
  const { data: roleData, isLoading: roleLoading } = useQuery({
    queryKey: ['role', id],
    queryFn: () => rolesApi.getById(id!),
    enabled: !!id && (isEditMode || isViewMode),
  })

  // Initialize permissions when screens load (for create mode)
  useEffect(() => {
    if (screens.length > 0 && isCreateMode) {
      const initialPermissions: PermissionState = {}
      screens.forEach(screen => {
        initialPermissions[screen.screen_id] = {
          can_view: false,
          can_add_edit: false,
          can_approve: false,
        }
      })
      setPermissions(initialPermissions)
    }
  }, [screens, isCreateMode])

  // Populate form when role data loads
  useEffect(() => {
    if (roleData && (isEditMode || isViewMode)) {
      setRoleName(roleData.role_name)
      setRoleDescription(roleData.role_description || '')
      
      const permState: PermissionState = {}
      // Initialize all screens with false
      screens.forEach(screen => {
        permState[screen.screen_id] = {
          can_view: false,
          can_add_edit: false,
          can_approve: false,
        }
      })
      // Override with actual permissions
      roleData.permissions.forEach(perm => {
        permState[perm.screen_id] = {
          can_view: perm.can_view,
          can_add_edit: perm.can_add_edit,
          can_approve: perm.can_approve,
        }
      })
      setPermissions(permState)
    }
  }, [roleData, screens, isEditMode, isViewMode])

  // Group screens by screen_group
  const groupedScreens = screens.reduce((acc, screen) => {
    if (!acc[screen.screen_group]) {
      acc[screen.screen_group] = []
    }
    acc[screen.screen_group].push(screen)
    return acc
  }, {} as Record<string, Screen[]>)

  const createMutation = useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      toast.success('Role created successfully')
      navigate('/uam/roles')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create role'
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRequest }) =>
      rolesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      queryClient.invalidateQueries({ queryKey: ['role', id] })
      toast.success('Role updated successfully')
      navigate('/uam/roles')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update role'
      toast.error(errorMessage)
    },
  })

  const handlePermissionChange = (screenId: string, field: 'can_view' | 'can_add_edit' | 'can_approve', value: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [screenId]: {
        ...prev[screenId],
        [field]: value,
      },
    }))
  }

  const handleSelectAll = (field: 'can_view' | 'can_add_edit' | 'can_approve', value: boolean) => {
    setPermissions(prev => {
      const updated = { ...prev }
      screens.forEach(screen => {
        updated[screen.screen_id] = {
          ...updated[screen.screen_id],
          [field]: value,
        }
      })
      return updated
    })
  }

  // Check if all screens have a specific permission
  const isAllSelected = (field: 'can_view' | 'can_add_edit' | 'can_approve'): boolean => {
    return screens.length > 0 && screens.every(s => permissions[s.screen_id]?.[field])
  }

  // Check if some (but not all) screens have a specific permission
  const isSomeSelected = (field: 'can_view' | 'can_add_edit' | 'can_approve'): boolean => {
    const selected = screens.filter(s => permissions[s.screen_id]?.[field])
    return selected.length > 0 && selected.length < screens.length
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const permissionsList: PermissionInput[] = Object.entries(permissions)
      .filter(([_, perm]) => perm.can_view || perm.can_add_edit || perm.can_approve)
      .map(([screenId, perm]) => ({
        screen_id: screenId,
        can_view: perm.can_view,
        can_add_edit: perm.can_add_edit,
        can_approve: perm.can_approve,
      }))

    if (isEditMode && id) {
      updateMutation.mutate({
        id,
        data: {
          role_name: roleName,
          role_description: roleDescription || undefined,
          permissions: permissionsList,
        },
      })
    } else {
      const createData: CreateRoleRequest = {
        role_name: roleName,
        role_description: roleDescription || undefined,
        permissions: permissionsList,
      }
      createMutation.mutate(createData)
    }
  }

  if (screensLoading || (id && roleLoading)) {
    return <div className="text-center py-8">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/uam/roles')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isViewMode ? 'View Role' : isEditMode ? 'Edit Role' : 'Create Role'}
          </h1>
          <p className="text-muted-foreground">
            {isViewMode ? 'View role details and permissions' : 'Configure role name and permissions'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Role Information</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="role_name">Role Name *</Label>
              <Input
                id="role_name"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g., Admin, Manager, Viewer"
                required
                disabled={isViewMode}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role_description">Description</Label>
              <Textarea
                id="role_description"
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                placeholder="Optional description for this role"
                rows={1}
                disabled={isViewMode}
              />
            </div>
          </CardContent>
        </Card>

        {/* Permissions */}
        <Card>
          <CardHeader>
            <CardTitle>Permissions</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[300px]">Module</TableHead>
                  <TableHead className="text-center w-[120px]">
                    <div className="flex items-center justify-center gap-2">
                      {!isViewMode && (
                        <Checkbox
                          checked={isAllSelected('can_view')}
                          ref={(el) => {
                            if (el) {
                              (el as unknown as HTMLButtonElement).dataset.indeterminate = isSomeSelected('can_view') ? 'true' : 'false'
                            }
                          }}
                          className={isSomeSelected('can_view') ? 'data-[state=unchecked]:bg-primary/50' : ''}
                          onCheckedChange={(checked) => handleSelectAll('can_view', checked as boolean)}
                        />
                      )}
                      <span>View</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[120px]">
                    <div className="flex items-center justify-center gap-2">
                      {!isViewMode && (
                        <Checkbox
                          checked={isAllSelected('can_add_edit')}
                          className={isSomeSelected('can_add_edit') ? 'data-[state=unchecked]:bg-primary/50' : ''}
                          onCheckedChange={(checked) => handleSelectAll('can_add_edit', checked as boolean)}
                        />
                      )}
                      <span>Add/Edit</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[120px]">
                    <div className="flex items-center justify-center gap-2">
                      {!isViewMode && (
                        <Checkbox
                          checked={isAllSelected('can_approve')}
                          className={isSomeSelected('can_approve') ? 'data-[state=unchecked]:bg-primary/50' : ''}
                          onCheckedChange={(checked) => handleSelectAll('can_approve', checked as boolean)}
                        />
                      )}
                      <span>Approve</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(groupedScreens).map(([group, groupScreens]) => (
                  <Fragment key={group}>
                    {/* Group Header Row */}
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableCell colSpan={4} className="font-semibold text-base py-3">
                        {group}
                      </TableCell>
                    </TableRow>
                    {/* Screen Rows */}
                    {groupScreens.map((screen) => (
                      <TableRow key={screen.screen_id}>
                        <TableCell className="font-medium pl-6">{screen.screen_name}</TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={permissions[screen.screen_id]?.can_view || false}
                            onCheckedChange={(checked) =>
                              handlePermissionChange(screen.screen_id, 'can_view', checked as boolean)
                            }
                            disabled={isViewMode}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={permissions[screen.screen_id]?.can_add_edit || false}
                            onCheckedChange={(checked) =>
                              handlePermissionChange(screen.screen_id, 'can_add_edit', checked as boolean)
                            }
                            disabled={isViewMode}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={permissions[screen.screen_id]?.can_approve || false}
                            onCheckedChange={(checked) =>
                              handlePermissionChange(screen.screen_id, 'can_approve', checked as boolean)
                            }
                            disabled={isViewMode}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Actions */}
        {!isViewMode && (
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/uam/roles')}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : isEditMode
                ? 'Update Role'
                : 'Create Role'}
            </Button>
          </div>
        )}

        {isViewMode && (
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/uam/roles')}>
              Back to Roles
            </Button>
            <Button type="button" onClick={() => navigate(`/uam/roles/${id}/edit`)}>
              Edit Role
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}

